import React, { createContext, useContext, useState, ReactNode, useMemo, useEffect } from 'react'
import { useFlowNotesPubSub } from '@/hooks/pub-sub/use-flow-notes-pub-sub'
import { useFlowNoteFetch } from '@/hooks/use-flow-note-fetch'
import { useHall } from './HallProvider'
import { useTurns } from './TurnsProvider'
import { DebateFormat, TurnType, generateInitialFlowNotes, parseDebateFormat } from '@db8/shared'

export interface Row {
  id: string
  title: string
  content: string
}

export interface ColumnWithRows {
  id: number
  rows: Row[]
}

export interface Page {
  id: string
  name: string
  side: DebateSide
  columns: ColumnWithRows[]
}
export type DebateSide = 'AFF' | 'NEG' | 'AI'

interface FlowNotesContextProps {
  pages: Page[]
  currentPageId: string
  currentPage: Page | undefined
  setCurrentPageId: (id: string) => void
  addPage: (page: Page) => void
  removePage: (pageId: string) => void
  updatePage: (pageId: string, newData: Partial<Page>) => void
  addRow: (pageId: string, columnId: number, row: Row) => void
  removeRow: (pageId: string, columnId: number, rowId: string) => void
  updateRow: (pageId: string, columnId: number, rowId: string, newData: Partial<Row>) => void
  setPages: (pages: Page[]) => void
  resetFlowNotes: (format?: DebateFormat) => void
  fetchFlowNote: (opts?: { transcript?: string; step?: number; turnType?: TurnType }) => void
}

const FlowNotesContext = createContext<FlowNotesContextProps | undefined>(undefined)

interface FlowNotesProviderProps {
  children: ReactNode
}

export const FlowNotesProvider: React.FC<FlowNotesProviderProps> = ({ children }) => {
  const [pages, setPages] = useState<Page[]>([])
  const [currentPageId, setCurrentPageId] = useState<string>('ai-judge-notes')

  const { publishFlowNotes } = useFlowNotesPubSub({ pages, setPages })

  const setPagesAndPublish = (updateFn: (prev: Page[]) => Page[]) => {
    setPages((prev) => {
      const newPages = updateFn(prev)
      publishFlowNotes(newPages)
      return newPages
    })
  }

  const addPage = (page: Page) => {
    setPagesAndPublish((prev) => [...prev, page])
  }

  const removePage = (pageId: string) => {
    setPagesAndPublish((prev) => {
      const filtered = prev.filter((p) => p.id !== pageId)
      return filtered
    })
    if (currentPageId === pageId && pages.length > 1) {
      setCurrentPageId(pages[0].id)
    }
  }

  const updatePage = (pageId: string, newData: Partial<Page>) => {
    setPagesAndPublish((prev) => prev.map((p) => (p.id === pageId ? { ...p, ...newData } : p)))
  }

  const addRow = (pageId: string, columnId: number, row: Row) => {
    setPagesAndPublish((prev) =>
      prev.map((p) =>
        p.id === pageId
          ? {
              ...p,
              columns: p.columns.map((col) =>
                col.id === columnId ? { ...col, rows: [...col.rows, row] } : col,
              ),
            }
          : p,
      ),
    )
  }

  const removeRow = (pageId: string, columnId: number, rowId: string) => {
    setPagesAndPublish((prev) =>
      prev.map((p) =>
        p.id === pageId
          ? {
              ...p,
              columns: p.columns.map((col) =>
                col.id === columnId
                  ? { ...col, rows: col.rows.filter((r) => r.id !== rowId) }
                  : col,
              ),
            }
          : p,
      ),
    )
  }

  const updateRow = (pageId: string, columnId: number, rowId: string, newData: Partial<Row>) => {
    setPagesAndPublish((prev) =>
      prev.map((p) =>
        p.id === pageId
          ? {
              ...p,
              columns: p.columns.map((col) =>
                col.id === columnId
                  ? {
                      ...col,
                      rows: col.rows.map((r) => (r.id === rowId ? { ...r, ...newData } : r)),
                    }
                  : col,
              ),
            }
          : p,
      ),
    )
  }

  const currentPage = useMemo(
    () => pages?.find((p) => p.id === currentPageId),
    [currentPageId, pages],
  )

  const aiJudgePage = useMemo(() => pages.find((p) => p.side === 'AI'), [pages])
  const { fetchFlowNote } = useFlowNoteFetch({ addRow, updateRow, aiJudgePage })

  const resetFlowNotes = (defaultFlowNotes) => {
    const parsedFlowNotes = JSON.parse(defaultFlowNotes.content)

    setPages(parsedFlowNotes)
    setCurrentPageId(parsedFlowNotes[0].id)
  }
  return (
    <FlowNotesContext.Provider
      value={{
        pages,
        currentPageId,
        currentPage,
        setCurrentPageId,
        addPage,
        removePage,
        updatePage,
        addRow,
        removeRow,
        updateRow,
        setPages,
        resetFlowNotes,
        fetchFlowNote,
      }}
    >
      {(() => {
        try {
          // expose for PDF export (read-only)
          ;(window as any).__FLOW_NOTES_PAGES__ = pages
        } catch {}
        return children
      })()}
    </FlowNotesContext.Provider>
  )
}

export const useFlowNotes = (): FlowNotesContextProps => {
  const context = useContext(FlowNotesContext)
  if (!context) {
    throw new Error('useFlowNotes must be used within a FlowNotesProvider')
  }
  return context
}
